import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ServiceCard = ({ service, onEdit, onDelete, onToggleStatus, highlightServiceId }) => {
  const { t } = useTranslation();
  const isHighlighted = highlightServiceId != null && String(service?.id) === String(highlightServiceId);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: service?.name,
    price: service?.price,
    duration: service?.duration
  });

  const handleSave = () => {
    onEdit(service?.id, editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({
      name: service?.name,
      price: service?.price,
      duration: service?.duration
    });
    setIsEditing(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON'
    })?.format(price);
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  return (
    <div
      data-service-row={service?.id}
      className={`bg-background border border-border rounded-lg p-6 hover-elevate transition-all duration-200 ${isHighlighted ? "ring-2 ring-primary bg-primary/10" : ""}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {isEditing ? (
            <Input
              type="text"
              value={editData?.name}
              onChange={(e) => setEditData({ ...editData, name: e?.target?.value })}
              className="mb-2"
            />
          ) : (
            <h3 className="text-lg font-semibold text-foreground mb-2">{service?.name}</h3>
          )}
          <p className="text-sm text-text-secondary mb-3">{service?.description}</p>
        </div>
        <div className="flex items-center space-x-2 ml-4">
          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
            service?.isActive 
              ? 'bg-success/10 text-success' :'bg-muted text-text-secondary'
          }`}>
            {service?.isActive ? t('common.active') : t('common.inactive')}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">
            {t('common.price')}
          </label>
          {isEditing ? (
            <Input
              type="number"
              value={editData?.price}
              onChange={(e) => setEditData({ ...editData, price: parseFloat(e?.target?.value) })}
              className="mt-1"
            />
          ) : (
            <p className="text-lg font-semibold text-foreground mt-1">
              {formatPrice(service?.price)}
            </p>
          )}
        </div>
        <div>
          <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">
            {t('common.duration')}
          </label>
          {isEditing ? (
            <Input
              type="number"
              value={editData?.duration}
              onChange={(e) => setEditData({ ...editData, duration: parseInt(e?.target?.value) })}
              className="mt-1"
            />
          ) : (
            <p className="text-lg font-semibold text-foreground mt-1">
              {formatDuration(service?.duration)}
            </p>
          )}
        </div>
      </div>
      <div className="mb-4">
        <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">
          {t('services.assignedStaff')}
        </label>
        <div className="flex flex-wrap gap-2 mt-2">
          {service?.assignedStaff?.map((staff) => (
            <div key={staff?.id} className="flex items-center space-x-2 bg-muted px-3 py-1 rounded-full">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <Icon name="User" size={12} color="white" />
              </div>
              <span className="text-sm font-medium text-foreground">{staff?.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">
            {t('services.bookingsThisMonth', { count: service?.bookingsThisMonth ?? 0 })}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {isEditing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button variant="default" size="sm" onClick={handleSave}>
                {t('common.save')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onToggleStatus(service?.id)}
                className="w-8 h-8"
              >
                <Icon name={service?.isActive ? "EyeOff" : "Eye"} size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditing(true)}
                className="w-8 h-8"
              >
                <Icon name="Edit" size={16} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(service?.id)}
                className="w-8 h-8 text-error hover:text-error"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;